<template>
  <div class="col-sm-12">
    <div class="card">
      <div class="card-body">

        <!-- Download Section -->
        <div class="dropdown-download-container">
          <div class="dropdown-item">
            <label for="monthYearPicker">Select Year & Month</label>
            <input
              type="month"
              v-model="monthYear"
              class="search-input"
              :max="currentMonthYear"
              @change="handleMonthYearChange"
              id="monthYearPicker"
            />
          </div>

          <button class="download-button" @click="downloadExcel" :disabled="downloading">
            <span v-if="downloading" class="spinner-border spinner-border-sm"></span>
            <span v-else>
              <i class="fa-solid fa-arrow-down"></i>
              Download Template
            </span>
          </button>
        </div>

        <!-- Upload Section -->
        <div class="file-submit-container">
          <input type="file" ref="fileInput" @change="handleFileUpload" accept=".xls,.xlsx" />

          <div class="submit-container">
            <button
              class="btn button-default"
              @click="handleSubmit"
              :disabled="submitting || !fileUploaded || !isFileValid"
            >
              <span v-if="submitting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              <span v-else>Submit</span>
            </button>
          </div>
        </div>

        <!-- Preview Table -->
        <div v-if="tableData.length" class="table-preview-wrapper">
          <div class="table-preview-header">
            <span class="table-preview-title">
              <i class="fa-solid fa-table"></i>
              Preview — {{ tableData.length }} row(s)
            </span>
            <span class="table-preview-hint">
              <i class="fa-solid fa-arrows-left-right"></i>
              Scroll horizontally to see all columns
            </span>
          </div>
          <div class="table-scroll-container" style="overflow-y: visible; max-height: none;">
            <table class="preview-table">
              <thead>
                <tr>
                  <th v-for="(header, index) in headers" :key="index">{{ header }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, rowIndex) in tableData" :key="rowIndex">
                  <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{ cell }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { downloadTargetMatrix, fetchDistributors, saveTarget } from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  data() {
    return {
      tableData: [],
      headers: [],
      variantIdRow: [],
      isFileValid: false,

      token: localStorage.getItem('token_set'),
      companyId: localStorage.getItem('company_id'),
      profileId: localStorage.getItem('profile_id'),

      downloading: false,
      fileUploaded: false,
      submitting: false,

      distributors: [],
      loading1: false,

      currentMonthYear: new Date().toISOString().slice(0, 7),
    };
  },

  created() {
    this.fetchDistributorsList();
  },

  setup() {
    const toast = useToast();
    return {
      toast,
      monthYear: ""
    };
  },

  methods: {

    async fetchDistributorsList() {
      try {
        this.loading1 = true;
        const response = await fetchDistributors(this.profileId, this.token);
        this.distributors = response.data.data;
      } catch (e) {
        console.log("error", e);
      } finally {
        this.loading1 = false;
      }
    },

    handleMonthYearChange() {
      this.resetFileState();
    },

    resetFileState() {
      this.tableData    = [];
      this.headers      = [];
      this.variantIdRow = [];
      this.isFileValid  = false;
      this.fileUploaded = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    async downloadExcel() {
      if (!this.monthYear) {
        this.toast.error("Please select a year and month first.");
        return;
      }

      try {
        this.downloading = true;
        const [year, month] = this.monthYear.split("-").map(Number);

        const response = await downloadTargetMatrix({ year, month }, this.token);

        if (response.data.status === 'success') {
          const rows  = response.data.data.rows;
          const items = response.data.data.items;

          if (!rows.length) {
            this.toast.error("No data found");
            return;
          }

          const sortedRows = [...rows].sort((a, b) => {
            if (a.fsm < b.fsm) return -1; if (a.fsm > b.fsm) return 1;
            if (a.asm < b.asm) return -1; if (a.asm > b.asm) return 1;
            if (a.distributor_name < b.distributor_name) return -1;
            if (a.distributor_name > b.distributor_name) return 1;
            return 0;
          });

          const typeGroups = [];
          const typeMap    = {};
          items.forEach(item => {
            if (!typeMap[item.type_id]) {
              typeMap[item.type_id] = { type_id: item.type_id, type_name: item.name, items: [] };
              typeGroups.push(typeMap[item.type_id]);
            }
            typeMap[item.type_id].items.push(item);
          });

          const fixedCols = [
            { header: "FSM",              key: "fsm" },
            { header: "ASM",              key: "asm" },
            { header: "Distributor ID",   key: "distributor_id" },
            { header: "Distributor Name", key: "distributor_name" },
            { header: "Year",             key: "year" },
            { header: "Month",            key: "month" },
          ];

          const FIXED_COL_COUNT = 6;
          const HEADER_ROW_1    = 1;
          const HEADER_ROW_2    = 2;
          const HEADER_ROW_3    = 3;
          const DATA_START_ROW  = 4;

          const ws      = {};
          const merges  = [];

          const colLetter = (idx) => {
            let letter = '', n = idx;
            while (n >= 0) {
              letter = String.fromCharCode((n % 26) + 65) + letter;
              n = Math.floor(n / 26) - 1;
            }
            return letter;
          };

          const setCell = (col, row, value, style = {}) => {
            const ref = `${colLetter(col)}${row}`;
            ws[ref] = { v: value, t: typeof value === 'number' ? 'n' : 's', s: style };
          };

          const styleTypeHeader  = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "2F5496" } }, alignment: { horizontal: "center", vertical: "center" } };
          const styleItemHeader  = { font: { bold: true }, fill: { fgColor: { rgb: "BDD7EE" } }, alignment: { horizontal: "center", vertical: "center", wrapText: true } };
          const styleIdHeader    = { font: { italic: true, color: { rgb: "888888" }, sz: 9 }, fill: { fgColor: { rgb: "F2F2F2" } }, alignment: { horizontal: "center" } };
          const styleFixedHeader = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "1F3864" } }, alignment: { horizontal: "center", vertical: "center" } };

          fixedCols.forEach((col, i) => {
            setCell(i, HEADER_ROW_1, col.header, styleFixedHeader);
            setCell(i, HEADER_ROW_2, "", styleFixedHeader);
            setCell(i, HEADER_ROW_3, "", styleFixedHeader);
            merges.push({ s: { r: HEADER_ROW_1 - 1, c: i }, e: { r: HEADER_ROW_3 - 1, c: i } });
          });

          let colIndex = FIXED_COL_COUNT;
          typeGroups.forEach(group => {
            const startCol = colIndex;
            group.items.forEach(item => {
              setCell(colIndex, HEADER_ROW_2, item.variant_name, styleItemHeader);
              setCell(colIndex, HEADER_ROW_3, item.variant_id, styleIdHeader);
              colIndex++;
            });
            const endCol = colIndex - 1;
            setCell(startCol, HEADER_ROW_1, group.type_name, styleTypeHeader);
            if (startCol < endCol) {
              for (let c = startCol + 1; c <= endCol; c++) setCell(c, HEADER_ROW_1, "", styleTypeHeader);
              merges.push({ s: { r: HEADER_ROW_1 - 1, c: startCol }, e: { r: HEADER_ROW_1 - 1, c: endCol } });
            }
          });

          const TOTAL_COLS = colIndex;
          let lastFsm = null, lastAsm = null;

          sortedRows.forEach((row, rowIdx) => {
            const isSameFsm = row.fsm === lastFsm;
            const isSameAsm = row.asm === lastAsm && isSameFsm;
            const excelRow  = DATA_START_ROW + rowIdx;

            setCell(0, excelRow, isSameFsm ? "" : row.fsm);
            setCell(1, excelRow, isSameAsm ? "" : row.asm);
            setCell(2, excelRow, row.distributor_id);
            setCell(3, excelRow, row.distributor_name);
            setCell(4, excelRow, row.year);
            setCell(5, excelRow, row.month);

            let itemColIndex = FIXED_COL_COUNT;
            typeGroups.forEach(group => {
              group.items.forEach(item => {
                const val = row[item.variant_name] ?? null;
                setCell(itemColIndex, excelRow, val === null ? "" : val);
                itemColIndex++;
              });
            });

            lastFsm = row.fsm;
            lastAsm = row.asm;
          });

          const totalRows = DATA_START_ROW + sortedRows.length - 1;
          ws['!ref']    = `A1:${colLetter(TOTAL_COLS - 1)}${totalRows}`;
          ws['!merges'] = merges;

          const colWidths = [];
          fixedCols.forEach(col => colWidths.push({
            wch: Math.max(col.header.length + 2, 18),
            hidden: col.key === 'distributor_id'
          }));
          items.forEach(item => colWidths.push({ wch: Math.max(item.variant_name.length + 2, 14) }));
          ws['!cols'] = colWidths;
          ws['!rows'] = [{ hpt: 22 }, { hpt: 30 }, { hpt: 0, hidden: true }];

          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, ws, "Target Matrix");
          XLSX.writeFile(workbook, `target_template_${year}_${month}.xlsx`);

        } else {
          this.toast.error("Failed to generate template.");
        }
      } catch (error) {
        console.error(error);
        this.toast.error("Error downloading file.");
      } finally {
        this.downloading = false;
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      // Reset previous state
      this.tableData    = [];
      this.headers      = [];
      this.variantIdRow = [];
      this.isFileValid  = false;
      this.fileUploaded = false;

      if (!this.monthYear) {
        this.toast.error("Please select a year and month before uploading.");
        if (this.$refs.fileInput) this.$refs.fileInput.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const data     = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet    = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

        const variantNameRow = jsonData[1] || [];
        const variantIdRow   = jsonData[2] || [];
        const dataRows       = jsonData.slice(3);

        const FIXED_COL_COUNT           = 6;
        const [selectedYear, selectedMonth] = this.monthYear.split("-").map(Number);

        // ── Check 1: Year & month must match selected period ──
        const firstDataRow = dataRows.find(row =>
          row[2] !== "" && row[2] !== null && row[2] !== undefined
        );

        if (firstDataRow) {
          const fileYear  = parseInt(firstDataRow[4]);
          const fileMonth = parseInt(firstDataRow[5]);

          if (fileYear !== selectedYear || fileMonth !== selectedMonth) {
            this.toast.error(
              `Wrong file period: file is for ${fileYear}/${String(fileMonth).padStart(2, '0')} ` +
              `but selected period is ${selectedYear}/${String(selectedMonth).padStart(2, '0')}. ` +
              `Please download the correct template.`
            );
            if (this.$refs.fileInput) this.$refs.fileInput.value = '';
            return;
          }
        }

        // ── Build column name map for error messages ──
        const columnNameMap = {};
        variantNameRow.forEach((name, idx) => {
          if (idx >= FIXED_COL_COUNT && name !== "") {
            columnNameMap[idx] = name;
          }
        });

        // ── Check 2: Validate all target value cells ──
        const errors = [];
        let lastDistributorId = null;

        dataRows.forEach((row, rowIdx) => {
          const displayRow = rowIdx + 1; // 1-based row number for user

          const rawDistId = row[2];
          if (rawDistId !== "" && rawDistId !== null) {
            lastDistributorId = parseInt(rawDistId);
          }

          Object.keys(columnNameMap).forEach(colIdxStr => {
            const colIdx    = parseInt(colIdxStr);
            const cellValue = row[colIdx];
            const colName   = columnNameMap[colIdx];

            // Empty cells are fine — user left no target
            if (cellValue === "" || cellValue === null || cellValue === undefined) return;

            const strValue = String(cellValue).trim();

            if (/[a-zA-Z]/.test(strValue)) {
              errors.push(`Row ${displayRow}, "${colName}": Letters are not allowed. Found "${strValue}".`);
            } else if (/[^0-9.]/.test(strValue)) {
              errors.push(`Row ${displayRow}, "${colName}": Special characters are not allowed. Found "${strValue}".`);
            } else if (strValue.includes('.')) {
              errors.push(`Row ${displayRow}, "${colName}": Decimals are not allowed. Found "${strValue}". Enter a whole number.`);
            }
          });
        });

        // Store data so preview always shows
        const fixedHeaders = ["FSM", "ASM", "Distributor ID", "Distributor Name", "Year", "Month"];
        const itemHeaders  = variantNameRow.slice(6).filter(h => h !== "");
        this.headers = [...fixedHeaders, ...itemHeaders];
        this.variantIdRow = variantIdRow;
        this.tableData    = dataRows;
        this.fileUploaded = true;

        if (errors.length > 0) {
          const errorText = errors.map((e, i) => `${i + 1}. ${e}`).join('\n\n');
          this.toast.error(
            `${errors.length} validation error(s) found:\n\n${errorText}`,
            {
              timeout: false,
              closeOnClick: false,
            }
          );
          this.isFileValid = false;
        } else {
          this.toast.success("File validated successfully. Ready to submit.");
          this.isFileValid = true;
        }
      };

      reader.readAsArrayBuffer(file);
    },

    async handleSubmit() {
      try {
        this.submitting = true;

        if (!this.fileUploaded || !this.isFileValid) {
          this.toast.error("Please upload a valid file before submitting.");
          return;
        }

        const [year, month] = this.monthYear.split("-").map(Number);

        if (!year || !month) {
          this.toast.error("Please select a valid year and month.");
          return;
        }

        const FIXED_COL_COUNT = 6;
        const variantIdMap    = {};

        this.variantIdRow.forEach((cell, colIdx) => {
          if (colIdx >= FIXED_COL_COUNT && cell !== "" && cell !== null && cell !== undefined) {
            const parsed = parseInt(String(cell));
            if (!isNaN(parsed)) variantIdMap[colIdx] = parsed;
          }
        });

        if (Object.keys(variantIdMap).length === 0) {
          this.toast.error("Invalid file format. Variant ID reference row not found.");
          return;
        }

        const excelPayload    = [];
        let lastDistributorId = null;

        this.tableData.forEach(row => {
          const rawDistId            = row[2];
          const distributorProfileId = rawDistId !== "" && rawDistId !== null && rawDistId !== undefined
            ? parseInt(rawDistId)
            : lastDistributorId;

          if (!distributorProfileId) return;
          lastDistributorId = distributorProfileId;

          Object.entries(variantIdMap).forEach(([colIdx, variantId]) => {
            const targetValue = row[colIdx];
            if (targetValue === null || targetValue === undefined || targetValue === "") return;

            excelPayload.push({
              distributor_profile_id: distributorProfileId,
              year,
              month,
              variant_id: variantId,
              target: Number(targetValue),
            });
          });
        });

        if (excelPayload.length === 0) {
          this.toast.error("No target data found in the uploaded file.");
          return;
        }

        const finalPayload = {
          target_created_by: this.profileId,
          year,
          month,
          Excel: excelPayload
        };

        const response = await saveTarget(finalPayload, this.token);

        if (response.status === 200) {
          this.toast.success("Distributor targets saved successfully!");
          this.resetFileState();
        } else if (response.status === 422) {
          console.error("Validation errors:", response.data.errors);
          this.toast.error("Validation failed. Please check the file and try again.");
        } else {
          console.error("Server error:", response.data.error);
          this.toast.error("Server error. All changes were rolled back.");
        }

      } catch (e) {
        console.error("Error processing upload targets:", e);
        this.toast.error("Something went wrong. Please try again.");
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>
  
<style scoped>
.card {
  padding: 24px;
}

/* ── Download bar ── */
.dropdown-download-container {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.dropdown-item {
  flex: 1;
  min-width: 180px;
  max-width: 450px;
}

label {
  font-weight: bold;
  font-size: 14px;
}

.search-input {
  flex: 1;
  padding: 10px;
  margin-left: 10px;
  border-radius: 5px;
  border: 1px solid #ccc;
  font-size: 14px;
  transition: border-color 0.3s, box-shadow 0.3s;
}

.search-input:focus {
  border-color: #007bff;
  box-shadow: 0 0 5px rgba(38, 143, 255, 0.5);
  outline: none;
}

.download-button {
  margin-left: auto;
  background-color: #1D6F42;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 8px 16px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 14px;
}

.download-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── Upload bar ── */
.file-submit-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 16px;
  padding: 12px 16px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
}

/* ── Preview table wrapper ── */
.table-preview-wrapper {
  margin-top: 20px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
}

.table-preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  background-color: #f1f3f5;
  border-bottom: 1px solid #dee2e6;
}

.table-preview-title {
  font-weight: 600;
  font-size: 13px;
  color: #343a40;
  display: flex;
  align-items: center;
  gap: 6px;
}

.table-preview-hint {
  font-size: 11px;
  color: #868e96;
  display: flex;
  align-items: center;
  gap: 4px;
}

.table-scroll-container {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 450px;
  width: 100%;
}

/* ── Preview table ── */
.preview-table {
  border-collapse: collapse;
  white-space: nowrap;
  min-width: 100%;
  font-size: 13px;
}

.preview-table thead tr {
  position: sticky;
  top: 0;
  z-index: 2;
}

.preview-table th {
  padding: 10px 14px;
  background-color: #343a40;
  color: #ffffff;
  font-weight: 600;
  text-align: center;
  border: 1px solid #495057;
  white-space: nowrap;
}

.preview-table td {
  padding: 8px 14px;
  border: 1px solid #dee2e6;
  text-align: center;
  color: #212529;
  min-width: 120px;
}

.preview-table tbody tr:nth-child(even) {
  background-color: #f8f9fa;
}

.preview-table tbody tr:hover {
  background-color: #e8f4fd;
  transition: background-color 0.15s ease;
}

/* ── Toast line breaks ── */
:deep(.Vue-Toastification__toast-body) {
  white-space: pre-line;
}
</style>